import * as tldExtractor from 'tld-extract';
import { passwordStrength } from '@/constants/registration';

export const TLD_EXTRACTOR_EDGE_CASES = [
  'idf.il',
  'gov.il',
  'gov.au',
  'qld.gov.au',
  'gov.br',
  'sp.gov.br',
  'nhs.uk',
  'gov.sa',
];

// Two lists, kept in lockstep with cynergy-be (manual-configuration.types.ts):
//   BLOCKED_EMAIL_DOMAINS — checked against the email domain.
//   BLOCKED_COMPANY_DOMAINS — checked against `company_domain` (direct only).
// Both case-insensitive, match domain + every parent. Empty by default.
export const BLOCKED_EMAIL_DOMAINS = [];
export const BLOCKED_COMPANY_DOMAINS = [];

function domainMatches(domain, list) {
  if (!list.length) return false;
  const parts = domain.split('.');
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (list.includes(parts.slice(i).join('.'))) return true;
  }
  return false;
}

function extractDomainFromEmail(email) {
  if (!email || typeof email !== 'string') return null;
  const at = email.lastIndexOf('@');
  if (at < 0) return null;
  const domain = email.slice(at + 1).toLowerCase().trim();
  return domain || null;
}

export function isBlockedEmailDomain(email) {
  const domain = extractDomainFromEmail(email);
  return !!domain && domainMatches(domain, BLOCKED_EMAIL_DOMAINS);
}

export function isBlockedCompanyDomain(companyDomain) {
  if (!companyDomain || typeof companyDomain !== 'string') return false;
  const normalized = companyDomain.toLowerCase().trim();
  if (!normalized) return false;
  return domainMatches(normalized, BLOCKED_COMPANY_DOMAINS);
}
function isValidDomain(asset) {
  let assetParts = {};
  let assetWithProtocol = asset;
  if (!asset.startsWith('http')) {
    assetWithProtocol = `https://${asset}`;
  } else return false;

  if (TLD_EXTRACTOR_EDGE_CASES.includes(asset)) {
    const assetPartsAroundDot = asset.split('.');
    // eslint-disable-next-line prefer-destructuring
    assetParts.tld = assetPartsAroundDot.slice(assetPartsAroundDot.length > 2 ? -2 : -1).join('.');
    assetParts.domain = `${assetPartsAroundDot.slice(assetPartsAroundDot.length > 2 ? -3 : -2)[0]}.${assetParts.tld}`;
    assetParts.sub = '';
  } else {
    try {
      assetParts = tldExtractor(assetWithProtocol);
    } catch (e) {
      return false;
    }
  }

  if (assetParts.sub.length > 0) return false;
  if (assetParts.tld.length === 0) return false;
  if (assetParts.domain.endsWith('.')) return false;
  if (asset.startsWith('.')) return false;
  if (asset.includes('\\') || asset.includes('/')) return false;
  return true;
}

function isValidSubdomain(asset) {
  let assetWithProtocol = asset;
  if (!asset.startsWith('http')) {
    assetWithProtocol = `https://${asset}`;
  } else return false;
  let assetParts = [];
  try {
    assetParts = tldExtractor(assetWithProtocol);
  } catch (e) {
    return false;
  }
  if (asset.startsWith('.')) return false;
  if (assetParts.domain.endsWith('.')) return false;
  if (TLD_EXTRACTOR_EDGE_CASES.includes(assetParts.tld)) {
    const assetPartsAroundDot = asset.split('.');
    // eslint-disable-next-line prefer-destructuring
    assetParts.tld = assetPartsAroundDot.slice(-1)[0]; // suffix
    assetParts.domain = `${assetPartsAroundDot.slice(-2)[0]}.${assetParts.tld}`; // tld
    assetParts.sub = assetPartsAroundDot.slice(0, -2).join('.'); // rest
  }
  if (assetParts.sub.length === 0) return false;
  if (assetParts.tld.length === 0) return false;
  return true;
}
function checkEmailDomain(subdomain) {
  let assetParts = {};
  let assetWithProtocol = subdomain;
  if (!subdomain?.startsWith('http')) {
    assetWithProtocol = `https://${subdomain}`;
  } else return false;
  if (TLD_EXTRACTOR_EDGE_CASES.includes(subdomain)) {
    const assetPartsAroundDot = subdomain.split('.');
    // eslint-disable-next-line prefer-destructuring
    assetParts.tld = assetPartsAroundDot.slice(-1);
    assetParts.domain = `${assetPartsAroundDot.slice(-2)[0]}.${assetParts.tld}`;
    assetParts.sub = '';
  } else {
    try {
      assetParts = tldExtractor(assetWithProtocol);
    } catch (e) {
      return false;
    }
  }

  if (assetParts.tld.length === 0) return false;
  if (assetParts.domain.endsWith('.')) return false;
  if (subdomain.startsWith('.')) return false;
  if (subdomain.includes('\\') || subdomain.includes('/')) return false;
  return true;
}

function isValidEmailSubdomain(email) {
  const asset = email?.split('@')[1];
  const regex = /^(?=.{1,63}$)([a-zA-Z0-9](?!.*--)[a-zA-Z0-9-_]*\.)*[a-zA-Z0-9]*[a-zA-Z0-9-_]*[a-zA-Z0-9]+$/;
  return regex.test(asset) && checkEmailDomain(asset);
}

function isValidWebsite(website) {
  let assetWithProtocol = website;
  if (!website.startsWith('http')) {
    assetWithProtocol = `https://${website}`;
  }
  let assetParts = [];
  try {
    assetParts = tldExtractor(assetWithProtocol);
  } catch (e) {
    return false;
  }
  if (assetParts.sub.length === 0) return false;
  if (assetParts.tld.length === 0) return false;
  if (website.startsWith('.')) return false;
  if (assetParts.domain.endsWith('.')) return false;
  return true;
}

export function extractDomainFromUrl(url) {
  const urlParts = tldExtractor.parse_host(url);
  let assetParts = {};
  if (TLD_EXTRACTOR_EDGE_CASES.includes(urlParts.tld) &&
    urlParts.tld !== urlParts.domain &&
    urlParts.sub.length === 0) {
    const assetPartsAroundDot = url.split('.');
    // eslint-disable-next-line prefer-destructuring
    assetParts.tld = assetPartsAroundDot.slice(-1)[0];
    assetParts.domain = `${assetPartsAroundDot.slice(-2)[0]}.${assetParts.tld}`;
    assetParts.sub = assetPartsAroundDot.slice(0, -2).join('.'); // rest
  } else {
    assetParts = urlParts;
  }
  return assetParts;
}

function hostToDecimal(host) {
  const parts = host.split('.');
  // eslint-disable-next-line no-bitwise,radix
  const num = (parseInt(parts[0]) << 24) | (parseInt(parts[1]) << 16) | (parseInt(parts[2]) << 8) | parseInt(parts[3]);
  // eslint-disable-next-line no-bitwise
  return num >>> 0;
}

function isValidHostRange(host) {
  const startHost = host.split('-')[0].trim();
  const endHost = host.split('-')[1].trim();
  const startHostDecimal = hostToDecimal(startHost);
  const endHostDecimal = hostToDecimal(endHost);
  return endHostDecimal >= startHostDecimal;
}

export const rules = {
  // eslint-disable-next-line max-len
  virtualSubdomain: v => !!v && !!/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\.[a-zA-Z0-9-]+)+$/.test(v),
  urlPath: v => !!v && !!v.startsWith('/'),
  portNotRequired: v => !v || !!/^([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/.test(v),
  port: v => !!v && !!/^([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/.test(v),
  fqdn: v => !!v && !!/^(?!:\/\/)(?=.{1,255}$)((.{1,63}\.){1,127}(?![0-9]*$)[a-z0-9-]+\.?)$/.test(v),
  host: v => !!v && !!/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(v),
  // eslint-disable-next-line max-len
  hostWithRange: v => !!v && !!/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\/([0-9]|[1-2][0-9]|30|31|32))?$/.test(v),
  // eslint-disable-next-line max-len
  hostWithHyphenRange: v => !!v && !!/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\s*-?\s*(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(v),
  hostRangeValid: v => !!v && !!isValidHostRange(v),
  domain: v => !!v && !!/^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]{0,1}(?:\.[a-zA-Z]{2,})+$/.test(v),
  newDomainConfiguration: v => !!v && !!isValidDomain(v),
  subdomain: v => !!v && !!isValidSubdomain(v),
  email: v =>
    // eslint-disable-next-line implicit-arrow-linebreak
    !!v && !!/^[\w+-]+(\.[\w+-]+)*@([a-z0-9-]+(\.[a-z0-9-]+)*?\.[a-z]{2,}|(\d{1,3}\.){3}\d{1,3})(:\d{4})?$/.test(v),
  emailNotDenied: v => !v || !isBlockedEmailDomain(v),
  companyDomainNotBlocked: v => !v || !isBlockedCompanyDomain(v),
  emailSubdomain: v => !!v && !!isValidEmailSubdomain(v),
  phone: v => !!v && !!/^(([+][(]?[0-9]{1,3}[)]?)|([(]?[0-9]{4}[)]?))\s*[)]?[-\s.]?[(]?[0-9]{1,3}[)]?([-\s.]?[0-9]{3})([-\s.]?[0-9]{3,4})/.test(v),
  required: v => !!v,
  requiredNumber: v => !!v && /[0-9]/.test(v),
  requiredUpperCase: v => !!v && /[A-Z]/.test(v),
  requiredLowerCase: v => !!v && /[a-z]/.test(v),
  requiredSpecialSymbol: v => !!v && /[-+_!@#$%^&*.,?=:;'"]/.test(v),
  minLength: (length, message) => v => (!!v && v.length >= length) || message,
  website: v => !!v &&
    (!!/^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm.test(v) ||
      !!isValidWebsite(v)),
  linkedin: v => !!v && !!/^http(s)?:\/\/([\w]+\.)?linkedin\.com\/(in|company|events|pub)\/[A-z0-9_-]+\/?/.test(v),
  anyWebsite: v => !!v && !!(/^http(s)?:\/\/([\w]+\.)?linkedin\.com\/(in|company|events|pub)\/[A-z0-9_-]+\/?/.test(v) ||
    // eslint-disable-next-line max-len
    /(https?:\/\/)?(www\.)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)|(https?:\/\/)?(www\.)?(?!ww)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/.test(v)),
};

/**
 * Builds a subdomain asset from a typed prefix and its TLD. The TLD is appended
 * only for a bare prefix; a value that is itself a full domain under a different
 * TLD is returned unchanged so it fails validation instead of being grafted.
 */
export function buildSubdomainAsset(url, tld) {
  const normalizedUrl = (url || '').trim();
  const normalizedTld = (tld || '').trim();
  if (!normalizedUrl || !normalizedTld) {
    return '';
  }
  const lowerUrl = normalizedUrl.toLowerCase();
  const lowerTld = normalizedTld.toLowerCase();
  // Already the TLD itself or a subdomain of it — keep what was typed.
  if (lowerUrl === lowerTld || lowerUrl.endsWith(`.${lowerTld}`)) {
    return normalizedUrl;
  }
  try {
    // tld-extract is a CommonJS callable; tolerate both ESM-interop shapes.
    const extract = (tldExtractor && tldExtractor.default) ? tldExtractor.default : tldExtractor;
    const parts = extract(`https://${lowerUrl}`);
    // Typed value is a full foreign domain — do not append the selected TLD.
    if (parts && parts.tld && parts.tld.length > 0) {
      return normalizedUrl;
    }
  } catch (e) {
    // Bare prefix (e.g. "dev", "api.staging") — fall through and append the TLD.
  }
  return `${normalizedUrl}.${normalizedTld}`;
}

/**
 * A subdomain asset is valid only when a TLD is associated (required) and the
 * resulting asset is a strict subdomain of that TLD: it ends with `.<tld>`, has
 * at least one label before it, and every label is a valid hostname label.
 */
export function isSubdomainAssetValid(url, tld) {
  const normalizedTld = (tld || '').trim().toLowerCase();
  if (!normalizedTld) {
    return false;
  }
  const fullAsset = buildSubdomainAsset(url, tld).toLowerCase();
  if (!fullAsset || !fullAsset.endsWith(`.${normalizedTld}`)) {
    return false;
  }
  const prefix = fullAsset.slice(0, -(normalizedTld.length + 1));
  if (!prefix) {
    return false;
  }
  return prefix.split('.').every(label => /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(label));
}

export const validatePasswordStrength = password => {
  let level = 0;

  const levelsHash = {
    0: passwordStrength.none,
    1: passwordStrength.low,
    2: passwordStrength.low,
    3: passwordStrength.low,
    4: passwordStrength.medium,
    5: passwordStrength.medium,
    6: passwordStrength.safe,
  };

  const containsCapital = rules.requiredUpperCase(password);
  const containsNumber = rules.requiredNumber(password);
  const containsLower = rules.requiredLowerCase(password);
  const containsSymbol = rules.requiredSpecialSymbol(password);

  if (password?.length) {
    level += 1;
  }

  if (password?.length >= 15) {
    level += 1;
  }

  if (containsCapital) {
    level += 1;
  }

  if (containsNumber) {
    level += 1;
  }

  if (containsLower) {
    level += 1;
  }

  if (containsSymbol) {
    level += 1;
  }

  return levelsHash[level];
};

// AWS Cognito compliant password validation
export const validatePasswordCompliance = password => {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }

  const requirements = [];
  const errors = [];

  // Minimum length (8 characters)
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  requirements.push({
    text: 'At least 8 characters',
    met: password.length >= 8,
  });

  // Uppercase letter
  const hasUppercase = rules.requiredUpperCase(password);
  if (!hasUppercase) {
    errors.push('Password must contain at least one uppercase letter');
  }
  requirements.push({
    text: 'One uppercase letter (A-Z)',
    met: hasUppercase,
  });

  // Lowercase letter
  const hasLowercase = rules.requiredLowerCase(password);
  if (!hasLowercase) {
    errors.push('Password must contain at least one lowercase letter');
  }
  requirements.push({
    text: 'One lowercase letter (a-z)',
    met: hasLowercase,
  });

  // Number
  const hasNumber = rules.requiredNumber(password);
  if (!hasNumber) {
    errors.push('Password must contain at least one number');
  }
  requirements.push({
    text: 'One number (0-9)',
    met: hasNumber,
  });

  // Special character
  const hasSpecialChar = rules.requiredSpecialSymbol(password);
  if (!hasSpecialChar) {
    errors.push('Password must contain at least one special character');
  }
  requirements.push({
    text: 'One special character (!@#$%^&*)',
    met: hasSpecialChar,
  });

  return {
    isValid: errors.length === 0,
    error: errors.length > 0 ? errors[0] : null,
    requirements,
    allErrors: errors,
  };
};
